import { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  InputOtp,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@heroui/react";
import { useToast } from "@/hooks/use-toast";
import {
  useSendEmailCode,
  useVerifyEmailCode,
  useSendSmsCode,
  useVerifySmsCode,
} from "@/api/use-verification-api";
import { Mail, Phone, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface VerifyContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "email" | "phone";
  contact: string;
  onSuccess?: () => void;
}

export function VerifyContactDialog({
  open,
  onOpenChange,
  type,
  contact,
  onSuccess,
}: VerifyContactDialogProps) {
  const { t } = useTranslation("app");
  const { toast } = useToast();
  const [otp, setOtp] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [verified, setVerified] = useState(false);

  const sendEmailCode = useSendEmailCode();
  const verifyEmailCode = useVerifyEmailCode();
  const sendSmsCode = useSendSmsCode();
  const verifySmsCode = useVerifySmsCode();

  const sendCode = type === "email" ? sendEmailCode : sendSmsCode;
  const verifyCode = type === "email" ? verifyEmailCode : verifySmsCode;

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  useEffect(() => {
    if (!open) return;
    void Promise.resolve().then(() => {
      setOtp("");
      setCodeSent(false);
      setVerified(false);
      setCooldown(0);
    });
  }, [open]);

  const handleSendCode = async () => {
    try {
      await sendCode.mutateAsync();
      setCodeSent(true);
      setCooldown(60);
      toast({
        title: t("verifyContact.codeSentTitle"),
        description: t("verifyContact.codeSentDescription", { contact }),
      });
    } catch (error: unknown) {
      const errData = axios.isAxiosError<{
        cooldownRemaining?: number;
        error?: string;
      }>(error)
        ? error.response?.data
        : undefined;
      if (errData?.cooldownRemaining) setCooldown(errData.cooldownRemaining);
      toast({
        variant: "destructive",
        title: t("verifyContact.sendFailedTitle"),
        description: errData?.error ?? t("verifyContact.tryLater"),
      });
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      toast({
        variant: "destructive",
        title: t("verifyContact.invalidCodeTitle"),
        description: t("verifyContact.invalidCodeDescription"),
      });
      return;
    }
    try {
      await verifyCode.mutateAsync({ code: otp });
      setVerified(true);
      toast({
        title: t("verifyContact.verifiedTitle"),
        description: t("verifyContact.verifiedDescription", {
          type:
            type === "email"
              ? t("verifyContact.email").toLowerCase()
              : t("verifyContact.phone").toLowerCase(),
        }),
      });
      setTimeout(() => {
        onOpenChange(false);
        onSuccess?.();
      }, 1500);
    } catch (error: unknown) {
      const errData = axios.isAxiosError<{
        attemptsRemaining?: number;
        error?: string;
      }>(error)
        ? error.response?.data
        : undefined;
      toast({
        variant: "destructive",
        title: t("verifyContact.invalidCodeTitle"),
        description:
          errData?.attemptsRemaining !== undefined
            ? t("verifyContact.attemptsRemaining", {
                count: errData.attemptsRemaining,
              })
            : (errData?.error ?? t("verifyContact.tryAgain")),
      });
      setOtp("");
    }
  };

  const Icon = type === "email" ? Mail : Phone;
  const typeLabel =
    type === "email" ? t("verifyContact.email") : t("verifyContact.phone");

  if (verified) {
    return (
      <Modal
        isOpen={open}
        onOpenChange={onOpenChange}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="sm"
      >
        <ModalContent>
          <ModalBody className="py-8">
            <div className="flex flex-col items-center justify-center">
              <CheckCircle2 className="h-16 w-16 text-success mb-4" />
              <h2 className="text-xl font-semibold mb-2">
                {t("verifyContact.successTitle")}
              </h2>
              <p className="text-default-500 text-center">
                {t("verifyContact.successBody", {
                  type: typeLabel.toLowerCase(),
                })}
              </p>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      placement="center"
      backdrop="blur"
      disableAnimation
      size="sm"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            <span>
              {t("verifyContact.verifyTitle", {
                type: typeLabel.toLowerCase(),
              })}
            </span>
          </div>
          <span className="text-small text-default-500 font-normal">
            {codeSent
              ? t("verifyContact.enterCode", { contact })
              : t("verifyContact.willSendCode", { contact })}
          </span>
        </ModalHeader>
        <ModalBody className="gap-6 py-4">
          {!codeSent ? (
            <div className="flex flex-col items-center gap-4">
              <p className="text-small text-default-500 text-center">
                {t("verifyContact.clickToReceive")}
              </p>
              <Button
                onPress={handleSendCode}
                color="primary"
                fullWidth
                isDisabled={sendCode.isPending || cooldown > 0}
                isLoading={sendCode.isPending}
              >
                {sendCode.isPending
                  ? t("verifyContact.sending")
                  : cooldown > 0
                    ? t("verifyContact.resendIn", { seconds: cooldown })
                    : t("verifyContact.sendVerificationCode")}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-6">
              <InputOtp
                length={6}
                value={otp}
                onValueChange={setOtp}
                allowedKeys="^[0-9]*$"
                size="lg"
              />
              <div className="flex flex-col items-center gap-2 w-full">
                <Button
                  onPress={handleVerify}
                  color="primary"
                  fullWidth
                  isDisabled={otp.length !== 6 || verifyCode.isPending}
                  isLoading={verifyCode.isPending}
                >
                  {verifyCode.isPending
                    ? t("verifyContact.verifying")
                    : t("verifyContact.verify")}
                </Button>
                <Button
                  variant="light"
                  size="sm"
                  onPress={handleSendCode}
                  isDisabled={sendCode.isPending || cooldown > 0}
                >
                  {cooldown > 0
                    ? t("verifyContact.resendIn", { seconds: cooldown })
                    : t("verifyContact.resendCode")}
                </Button>
              </div>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
